import { experimental_extendTheme as extendTheme } from '@mui/material/styles';
import { deepmerge } from '@mui/utils';
import cssVarJson from './export.json';
import { fixThemeOptions } from '../../utils/theme';
import ProximaNovaBlack from './fonts/proxima-nova/proxima_nova_black.woff';
import ProximaNovaBlack2 from './fonts/proxima-nova/proxima_nova_black.woff2';
import ProximaNovaBold from './fonts/proxima-nova/proxima_nova_bold.woff';
import ProximaNovaBold2 from './fonts/proxima-nova/proxima_nova_bold.woff2';
import ProximaNovaExtrabold from './fonts/proxima-nova/proxima_nova_extrabold.woff';
import ProximaNovaExtrabold2 from './fonts/proxima-nova/proxima_nova_extrabold.woff2';
import ProximaNovaLight from './fonts/proxima-nova/proxima_nova_light.woff';
import ProximaNovaLight2 from './fonts/proxima-nova/proxima_nova_light.woff2';
import ProximaNovaRegular from './fonts/proxima-nova/proxima_nova_regular.woff';
import ProximaNovaRegular2 from './fonts/proxima-nova/proxima_nova_regular.woff2';
import ProximaNovaSemibold from './fonts/proxima-nova/proxima_nova_semibold.woff';
import ProximaNovaSemibold2 from './fonts/proxima-nova/proxima_nova_semibold.woff2';
import ProximaNovaThin from './fonts/proxima-nova/proxima_nova_thin.woff';
import ProximaNovaThin2 from './fonts/proxima-nova/proxima_nova_thin.woff2';
import ProximaNovaThinItalic from './fonts/proxima-nova/proxima_nova_thin_italic.woff';
import ProximaNovaThinItalic2 from './fonts/proxima-nova/proxima_nova_thin_italic.woff2';
const CSS_VAR_PREFIX = 'leafio'; // Needs to be unique to support theme nesting
const baseFontFamily = 'Proxima Nova, Helvetica, Arial, sans-serif';
const customizations = {
    cssVarPrefix: CSS_VAR_PREFIX,
    breakpoints: {
        values: {
            xs: 0,
            sm: 768,
            md: 998,
            lg: 1280,
            xl: 1920,
        },
    },
    typography: {
        fontFamily: 'Proxima Nova',
    },
    components: {
        // MuiAlert overrides to fix the broken logic in extend theme.
        MuiAlert: {
            styleOverrides: {
                standardError: {
                    backgroundColor: `var(--${CSS_VAR_PREFIX}-palette-error-light)`,
                },
                standardWarning: {
                    backgroundColor: `var(--${CSS_VAR_PREFIX}-palette-warning-light)`,
                },
                standardInfo: {
                    backgroundColor: `var(--${CSS_VAR_PREFIX}-palette-info-light)`,
                },
                standardSuccess: {
                    backgroundColor: `var(--${CSS_VAR_PREFIX}-palette-success-light)`,
                },
            },
        },
        MuiCssBaseline: {
            styleOverrides: `
        @font-face {
          font-family: 'Proxima Nova';
          font-weight: 900;
          src: url(${ProximaNovaBlack2}) format('woff2'), url(${ProximaNovaBlack}) format('woff');
          font-display: auto;
          font-style: normal;
          font-stretch: normal;
        }

        @font-face {
          font-family: 'Proxima Nova';
          font-weight: 800;
          src: url(${ProximaNovaExtrabold2}) format('woff2'), url(${ProximaNovaExtrabold}) format('woff');
          font-display: auto;
          font-style: normal;
          font-stretch: normal;
        }

        @font-face {
          font-family: 'Proxima Nova';
          font-weight: 700;
          src: url(${ProximaNovaBold2}) format('woff2'), url(${ProximaNovaBold}) format('woff');
          font-display: auto;
          font-style: normal;
          font-stretch: normal;
        }

        @font-face {
          font-family: 'Proxima Nova';
          font-weight: 600;
          src: url(${ProximaNovaSemibold2}) format('woff2'), url(${ProximaNovaSemibold}) format('woff');
          font-display: auto;
          font-style: normal;
          font-stretch: normal;
        }

        @font-face {
          font-family: 'Proxima Nova';
          font-weight: 400;
          src: url(${ProximaNovaRegular2}) format('woff2'), url(${ProximaNovaRegular}) format('woff');
          font-display: auto;
          font-style: normal;
          font-stretch: normal;
        }

        @font-face {
          font-family: 'Proxima Nova';
          font-weight: 300;
          src: url(${ProximaNovaLight2}) format('woff2'), url(${ProximaNovaLight}) format('woff');
          font-display: auto;
          font-style: normal;
          font-stretch: normal;
        }

        @font-face {
          font-family: 'Proxima Nova';
          font-weight: 100;
          src: url(${ProximaNovaThin2}) format('woff2'), url(${ProximaNovaThin}) format('woff');
          font-display: auto;
          font-style: normal;
          font-stretch: normal;
        }

        @font-face {
          font-family: 'Proxima Nova';
          font-weight: 100;
          src: url(${ProximaNovaThinItalic2}) format('woff2'), url(${ProximaNovaThinItalic}) format('woff');
          font-display: auto;
          font-style: italic;
          font-stretch: normal;
        }
      `,
        },
        // Temporary fixes to the export. Once the figma is fixed and exported properly, these should be removed
        MuiChip: {
            styleOverrides: {
                label: {
                    fontWeight: 700,
                    fontFamily: baseFontFamily,
                },
                icon: ({ theme: t, ownerState }) => ({
                    // The default color is inherited by the Chip CSS color, we want instead to be inherited by the chip color prop
                    color: ownerState.color && ownerState.color !== 'default'
                        ? t.palette[ownerState.color].dark
                        : t.palette.Avatar.defaultBg,
                }),
            },
        },
        MuiButton: {
            styleOverrides: {
                sizeLarge: ({ ownerState }) => ({
                    // Export bug causing variant outlined hover padding change
                    padding: ownerState.variant === 'text' ? '8px 11px' : '8px 22px',
                    '&.Mui-disabled': {
                        padding: ownerState.variant === 'text' ? '8px 11px' : '8px 22px',
                    },
                    // MUI export sets default widths for the ripple effect, partly showing it when not interacting with the button
                    '&:not(.Mui-disabled):not(.Mui-focusVisible):not(:hover) .MuiTouchRipple-root': {
                        width: '0 !important',
                    },
                    ':hover': {
                        padding: ownerState.variant === 'text' ? '8px 11px' : '8px 22px', // Export bug causing button hover padding change
                    },
                }),
                sizeMedium: ({ ownerState }) => ({
                    // Export bug causing variant outlined hover padding change
                    padding: ownerState.variant === 'text' ? '6px 8px' : '6px 16px',
                    '&.Mui-disabled': {
                        padding: ownerState.variant === 'text' ? '6px 8px' : '6px 16px',
                    },
                    // MUI export sets default widths for the ripple effect, partly showing it when not interacting with the button
                    '&:not(.Mui-disabled):not(.Mui-focusVisible):not(:hover) .MuiTouchRipple-root': {
                        width: '0 !important',
                    },
                    ':hover': {
                        padding: ownerState.variant === 'text' ? '6px 8px' : '6px 16px',
                    },
                }),
                sizeSmall: ({ ownerState }) => ({
                    // Export bug causing variant outlined hover padding change
                    padding: ownerState.variant === 'text' ? '4px 5px' : '4px 10px',
                    '&.Mui-disabled': {
                        padding: ownerState.variant === 'text' ? '4px 5px' : '4px 10px',
                    },
                    // MUI export sets default widths for the ripple effect, partly showing it when not interacting with the button
                    '&:not(.Mui-disabled):not(.Mui-focusVisible):not(:hover) .MuiTouchRipple-root': {
                        width: '0 !important',
                    },
                    ':hover': {
                        padding: ownerState.variant === 'text' ? '4px 5px' : '4px 10px',
                    },
                }),
            },
        },
    },
};
export const leafioJSON = fixThemeOptions(deepmerge(cssVarJson, customizations));
export const leafio = extendTheme(leafioJSON);
export default leafio;
//# sourceMappingURL=data:application/json;base64,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